import React, { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { Typography, FormControl, FormHelperText, InputLabel, Select, MenuItem, TextField, Stack } from '@mui/material';
import { StepWrapper, StyledCardSection, StepperControls, PhoneInputField } from '@fnol/library/src/components';
import {
    checkEmail,
    checkPhoneNumber,
    checkIfSerbianMobPhoneNumber,
    common,
    prepareCitiesList,
    prepareCountriesList,
    hasValueAndIsSalesForce,
    getIsSalesForce,
    getApplicationId,
} from '@fnol/library/src/lib';
import { useApi } from '@fnol/library/src/hooks';
import {
    constValues,
    legalPersonType,
    personType,
    requestType,
    requestTypeOptions,
    translations,
    nameMaxLength,
    surnameMaxLength,
    questionMaxLength,
} from '../../lib/consts';
import { prepareIndustrialClassifications, prepareLeasingEntities, preparePersonRoles } from '../../lib/helpers';
import { useValidateIndividual, useValidateLegalEntity } from '../../lib/customHooks';
import {
    getCities,
    getCountries,
    getIndustrialClassifications,
    getLeasingEntities,
    getPersonRelations,
    getPersonRoles,
} from '../../rpc/actions';
import Individual from '../Individual';
import LegalLeasingEntity from '../LegalLeasingEntity';
import LegalIndividualSelector from '../LegalIndividualSelector';
import { IndividualCleanup } from '../Individual/Individual';
import { Info } from '@mui/icons-material';

type GenericObject = { [key: string]: any };

type Props = {
    navigator: GenericObject[];
    legalEntityType: personType;
};

interface FormData {
    requestType: number;
    submitterType: legalPersonType;
    submitterIndustrialClassificationType: string;
    submitterLegalEntityName: string;
    submitterLegalEntityCountry: string | number;
    submitterLegalEntityMyCountry: string;
    submitterLegalEntityContactName: string;
    submitterLegalEntityContactLastName: string;
    submitterLegalEntityContactEmail: string;
    submitterLegalEntityContactPhone: string;
    countries: Array<GenericObject>;
    cities: Array<GenericObject>;
    reportedClaims: Array<GenericObject>;
    VATFormatError: boolean;
    legalEntityIdFieldFormatError: boolean;
    submitterIndividualPhone: string;
    submitterIndividualEmail: string;
    personIsDriver: boolean;
}

const SubmitterDetailScreen = ({ navigator, legalEntityType }: Props) => {
    const { values, errors, touched, setFieldValue, setErrors, setTouched } = useFormikContext<FormData>();

    const individualValidation = useValidateIndividual(personType.submitter);
    const legalEntityValidation = useValidateLegalEntity(personType.submitter);

    const industrialClassificationType = `${personType[legalEntityType]}IndustrialClassificationType`;

    const [currentStep, { goForward, goBack }] = navigator;
    const [preparedCountries, setPreparedCountries] = useState<any>();

    const saveCountries = (res: GenericObject) => {
        setFieldValue('countries', prepareCountriesList(res.data));
    };

    const savePersonRelations = (res: GenericObject) => {
        setFieldValue('personRelations', res.data);
    };

    const { response: countries, fetchMethod: fetchCountries } = useApi(() => getCountries(), saveCountries);

    const saveCities = (res: GenericObject) => {
        setFieldValue('cities', prepareCitiesList(res.data));
    };

    const { response: cities, fetchMethod: fetchCities } = useApi(() => getCities(), saveCities);

    const saveLeasingEntities = (res: GenericObject) => {
        setFieldValue('leasingEntities', prepareLeasingEntities(res.data));
    };

    const { response: leasingEntites, fetchMethod: fetchLeasingEntities } = useApi(
        () => getLeasingEntities(),
        saveLeasingEntities,
    );

    const savePersonRoles = (res: GenericObject) => {
        setFieldValue('submitterPersonRoles', preparePersonRoles(res.data, personType.submitter));
        setFieldValue('ownerPersonRoles', preparePersonRoles(res.data, personType.owner));
        setFieldValue('userPersonRoles', preparePersonRoles(res.data, personType.user));
        setFieldValue('driverPersonRoles', preparePersonRoles(res.data, personType.driver));
        setFieldValue('injuredPersonRoles', preparePersonRoles(res.data, personType.injured));
        setFieldValue('ownerBuildingPersonRoles', preparePersonRoles(res.data, personType.ownerBuilding));
    };

    const { response: personRoles, fetchMethod: fetchPersonRoles } = useApi(() => getPersonRoles(), savePersonRoles);

    const { response: personRelations, fetchMethod: fetchPersonRelations } = useApi(
        () => getPersonRelations(),
        savePersonRelations,
    );

    const saveIndustrialClassifications = (res: GenericObject) => {
        setFieldValue('industrialClassifications', prepareIndustrialClassifications(res.data));
    };

    const { response: industrialClassifications, fetchMethod: fetchIndustrialClassifications } = useApi(
        () => getIndustrialClassifications(),
        saveIndustrialClassifications,
    );

    const handleNext = (): void => {
        let valid = true;
        setTouched(
            {
                submitterType: true,
                requestType: true,
                ...individualValidation.touched,
                ...legalEntityValidation.touched,
            },
            false,
        );

        let existingErrors: GenericObject = {};

        if (!values.requestType) {
            existingErrors.requestType = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }

        if (values.submitterType === legalPersonType.initialValue) {
            existingErrors.submitterType = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }

        if (values.submitterType === legalPersonType.legal) {
            if (!values.submitterLegalEntityContactName) {
                existingErrors.submitterLegalEntityContactName = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }

            if (!values.submitterLegalEntityContactLastName) {
                existingErrors.submitterLegalEntityContactLastName = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }

            if (
                !values.submitterLegalEntityContactEmail &&
                !hasValueAndIsSalesForce(values.submitterLegalEntityContactPhone)
            ) {
                existingErrors.submitterLegalEntityContactEmail = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
            if (values.submitterLegalEntityContactEmail && !checkEmail(values.submitterLegalEntityContactEmail)) {
                existingErrors.submitterLegalEntityContactEmail = translations.validationMessages.EMAIL_ADDRESS_FORMAT;
                valid = false;
            }

            if (
                (!values.submitterLegalEntityContactPhone || values.submitterLegalEntityContactPhone.length <= 4) &&
                !hasValueAndIsSalesForce(values.submitterLegalEntityContactEmail)
            ) {
                existingErrors.submitterLegalEntityContactPhone = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
            if (
                (!checkIfSerbianMobPhoneNumber(values.submitterLegalEntityContactPhone) ||
                    !checkPhoneNumber(values.submitterLegalEntityContactPhone)) &&
                (!hasValueAndIsSalesForce(values.submitterLegalEntityContactEmail) ||
                    values.submitterLegalEntityContactPhone.length > 4)
            ) {
                existingErrors.submitterLegalEntityContactPhone =
                    translations.validationMessages.PHONE_NUMBER_FORMAT_SERBIAN;
                valid = false;
            }

            existingErrors = { ...existingErrors, ...legalEntityValidation.errors };
            if (!legalEntityValidation.isValid) valid = false;
        }

        if (values.submitterType === legalPersonType.individual) {
            existingErrors = { ...existingErrors, ...individualValidation.errors };
            if (!individualValidation.isValid) valid = false;
        }

        if (
            values.submitterType === legalPersonType.individual &&
            values.submitterIndividualPhone &&
            (!checkIfSerbianMobPhoneNumber(values.submitterIndividualPhone) ||
                !checkPhoneNumber(values.submitterIndividualPhone)) &&
            (!hasValueAndIsSalesForce(values.submitterIndividualEmail) || values.submitterIndividualPhone.length > 4)
        ) {
            existingErrors.submitterIndividualPhone = translations.validationMessages.PHONE_NUMBER_FORMAT_SERBIAN;
            valid = false;
        }

        setErrors(existingErrors);

        if (valid) {
            if (
                values.submitterType === legalPersonType.legal &&
                values['submitterIndustrialClassificationType'] !== constValues.industrialClassificationTypeLeasing
            ) {
                setFieldValue('submitterLegalNameForDisplay', values['submitterLegalEntityName']);
            }
            goForward();
        }
    };

    const handleGoBack = (): void => {
        goBack();
    };

    const handleChangeRequestType = (e: { target: { value: any } }): any => {
        setFieldValue('requestType', e.target.value);

        if (e.target.value !== requestType.Both) {
            setFieldValue('ownerIndividualReportingImmaterialDamage', null);
            setFieldValue('ownerBuildingIndividualReportingImmaterialDamage', null);
            setFieldValue('userIndividualReportingImmaterialDamage', null);
            setFieldValue('driverIndividualReportingImmaterialDamage', null);
        }

        if (e.target.value === requestType.ImmaterialDamage) {
            setFieldValue('damageType', '');
            setFieldValue('chassisNumber', '');
            setFieldValue('vehicleBrand', '');
            setFieldValue('vehicleModel', '');
            setFieldValue('vehicleReg', '');
            setFieldValue('vehicleProductionYear', '');
            setFieldValue('vehicleType', '');
            setFieldValue('isSubmitterOwnerOfVehicle', '');
            setFieldValue('isVehicleParked', '');
            setFieldValue('isVehicleDrivable', '');
            setFieldValue('howIsVehicleUsed', '');
            setFieldValue('vehicleDamageDescription', '');
            setFieldValue('vehicleOtherDamages', '');
            setFieldValue('vehicleService', '');
            setFieldValue('smsLink', '');
            setFieldValue('payment', '');
            setFieldValue('objectDescription', '');
            setFieldValue('objectConstructionYear', '');
            setFieldValue('objectLocation', '');
            setFieldValue('objectDamageDescription', '');
            setFieldValue('moreObjectDamagesDescription', '');
            setFieldValue('objectType', '');
            setFieldValue('objectOwnerSubmitter', '');

            IndividualCleanup(personType.user, setFieldValue);
            IndividualCleanup(personType.owner, setFieldValue);
            IndividualCleanup(personType.ownerBuilding, setFieldValue);
            IndividualCleanup(personType.driver, setFieldValue);

            setFieldValue('skipSteps', [3]);
        }

        if (e.target.value === requestType.MaterialDamage) {
            setFieldValue('skipSteps', [4]);
        }

        if (e.target.value === requestType.Both) {
            setFieldValue('skipSteps', []);
        }
    };

    const handleChangeSubmitterType = () => {
        if (values.submitterType === legalPersonType.individual) {
            setFieldValue('submitterLegalEntityContactName', '');
            setFieldValue('submitterLegalEntityContactLastName', '');
            setFieldValue('submitterLegalEntityContactEmail', '');
            setFieldValue('submitterLegalEntityContactPhone', constValues.dialCodeSerbia);
        }
    };

    useEffect(() => {
        fetchCountries();
        fetchCities();
        fetchLeasingEntities();
        fetchPersonRoles();
        fetchPersonRelations();
        fetchIndustrialClassifications();
    }, []);

    useEffect(() => {
        Array.isArray(values.countries) && setPreparedCountries(values.countries);
    }, [countries]);

    useEffect(() => {
        handleChangeSubmitterType();
    }, [values.submitterType]);

    /* useEffect(() => {
        Array.isArray(values.cities) && setPreparedCities(values.cities);
    }, [cities]); */

    const selectedLegalEntityCountry = preparedCountries?.find(
        (item: any) => item.id === parseInt(values.submitterLegalEntityCountry as string),
    );
    values.submitterLegalEntityMyCountry = selectedLegalEntityCountry?.text;

    return (
        <StepWrapper>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '18px' }} variant="caption">
                    {translations.REQUEST_TYPE}
                </Typography>

                <FormControl required={true}>
                    <InputLabel id="request-type-label">{translations.SELECT_REQUEST_TYPE}</InputLabel>

                    <Select
                        id="request-type"
                        labelId="request-type-label"
                        name="requestType"
                        value={values.requestType || ''}
                        label={translations.SELECT_REQUEST_TYPE}
                        error={!!(touched.requestType && errors.requestType)}
                        onChange={handleChangeRequestType}
                    >
                        {requestTypeOptions.map((option) => {
                            return (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.value}
                                </MenuItem>
                            );
                        })}
                    </Select>

                    {touched.requestType && errors.requestType ? (
                        <FormHelperText error={true}>
                            <>{errors.requestType}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>

                <Typography style={{ fontWeight: 'bold', fontSize: '18px', paddingTop: '16px' }} variant="caption">
                    {translations.CHOOSE_SUBMITTER_TYPE + ' *'}
                </Typography>

                <LegalIndividualSelector selectorType={personType.submitter} />

                {values.submitterType === legalPersonType.legal && (
                    <>
                        <LegalLeasingEntity legalEntityType={personType.submitter}></LegalLeasingEntity>

                        {values.submitterIndustrialClassificationType !== '' ? (
                            <>
                                <Typography
                                    style={{ fontWeight: 'bold', fontSize: '18px', paddingTop: '16px' }}
                                    variant="caption"
                                >
                                    {translations.legalInfo.CONTACT_INFO}
                                </Typography>
                                <FormControl>
                                    <Field
                                        name="submitterLegalEntityContactName"
                                        label={translations.legalInfo.NAME}
                                        as={TextField}
                                        inputProps={{
                                            maxLength: nameMaxLength,
                                        }}
                                        error={
                                            !!(
                                                touched.submitterLegalEntityContactName &&
                                                errors.submitterLegalEntityContactName
                                            )
                                        }
                                        required
                                    />
                                    {touched.submitterLegalEntityContactName &&
                                    errors.submitterLegalEntityContactName ? (
                                        <FormHelperText error={true}>
                                            <>{errors.submitterLegalEntityContactName}</>
                                        </FormHelperText>
                                    ) : null}
                                </FormControl>
                                <FormControl>
                                    <Field
                                        name="submitterLegalEntityContactLastName"
                                        label={translations.legalInfo.LAST_NAME}
                                        as={TextField}
                                        inputProps={{
                                            maxLength: surnameMaxLength,
                                        }}
                                        error={
                                            !!(
                                                touched.submitterLegalEntityContactLastName &&
                                                errors.submitterLegalEntityContactLastName
                                            )
                                        }
                                        required
                                    />
                                    {touched.submitterLegalEntityContactLastName &&
                                    errors.submitterLegalEntityContactLastName ? (
                                        <FormHelperText error={true}>
                                            <>{errors.submitterLegalEntityContactLastName}</>
                                        </FormHelperText>
                                    ) : null}
                                </FormControl>

                                {getIsSalesForce(getApplicationId()) && (
                                    <Stack direction="row" alignItems="top" spacing={2} marginTop={2} marginBottom={3}>
                                        <Info color="primary" />
                                        <Typography variant="body2" paddingTop={0.5}>
                                            {translations.validationMessages.CONTACT_REQUIRED}
                                        </Typography>
                                    </Stack>
                                )}

                                <FormControl>
                                    <PhoneInputField
                                        name="submitterLegalEntityContactPhone"
                                        specialLabel={
                                            !hasValueAndIsSalesForce(values.submitterLegalEntityContactEmail)
                                                ? translations.personalInfo.MOBILE_STAR
                                                : translations.personalInfo.MOBILE
                                        }
                                        country={'rs'}
                                        required={!hasValueAndIsSalesForce(values.submitterLegalEntityContactEmail)}
                                    />
                                </FormControl>
                                <FormControl>
                                    <Field
                                        name="submitterLegalEntityContactEmail"
                                        label={translations.legalInfo.CONTACT_EMAIL}
                                        type="email"
                                        as={TextField}
                                        inputProps={{
                                            maxLength: questionMaxLength,
                                        }}
                                        error={
                                            !!(
                                                touched.submitterLegalEntityContactEmail &&
                                                errors.submitterLegalEntityContactEmail
                                            )
                                        }
                                        required={!hasValueAndIsSalesForce(values.submitterLegalEntityContactPhone)}
                                    />
                                    {touched.submitterLegalEntityContactEmail &&
                                    errors.submitterLegalEntityContactEmail ? (
                                        <FormHelperText error={true}>
                                            <>{errors.submitterLegalEntityContactEmail}</>
                                        </FormHelperText>
                                    ) : null}
                                </FormControl>
                            </>
                        ) : null}
                    </>
                )}
                {values.submitterType === legalPersonType.individual && (
                    <Individual individualType={personType.submitter} />
                )}
            </StyledCardSection>
            <StepperControls
                onPrevious={handleGoBack}
                onNext={!values.personIsDriver ? handleNext : undefined}
                onNextLabel={common.NEXT}
            />
        </StepWrapper>
    );
};

export default SubmitterDetailScreen;
